import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import { GetAdminCompanyDeletePreviewAPI, DeleteAdminCompanyAPI } from '../../../../api/AdminAPI';

// Motivo do botão Excluir desabilitado na lista e na página da empresa.
export const DELETE_LOCKED_HINT =
  'Exclusão liberada para conta de teste ou depois que a carência do encerramento terminar';

/**
 * DeleteCompanyDialog - Diálogo de exclusão permanente de uma empresa (Admin Geral)
 *
 * Ao abrir, busca a prévia de exclusão (bloqueios, avisos, resumo de dados e arquivos).
 * Confirmação exige: digitar o nome exato da empresa, marcar o checkbox de ciência
 * e não haver bloqueios pendentes. Cancelar sempre funciona.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {{ id: number|string, name: string }} props.company
 * @param {Function} props.onCancel
 * @param {Function} props.onDeleted - chamado com a resposta de sucesso da API de exclusão
 */
function DeleteCompanyDialog({ isOpen, company, onCancel, onDeleted }) {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [typedName, setTypedName] = useState('');
  const [checked, setChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (!isOpen || !company) return;

    setPreview(null);
    setPreviewError('');
    setTypedName('');
    setChecked(false);
    setDeleteError('');
    setLoadingPreview(true);

    GetAdminCompanyDeletePreviewAPI(company.id, (response) => {
      if (!response || response.success === false) {
        setPreviewError(response?.error || 'Erro ao carregar a prévia de exclusão. Tente novamente.');
        setLoadingPreview(false);
        return;
      }
      setPreview(response);
      setLoadingPreview(false);
    });
  }, [isOpen, company]);

  const companyName = preview?.company?.name || company?.name || '';
  const blockers = preview?.blockers || [];
  const warnings = preview?.warnings || [];
  const summary = (preview?.summary || []).filter((item) => item.count > 0);
  const totalRecords = summary.reduce((acc, item) => acc + (item.count || 0), 0);
  const files = preview?.files || [];

  const canConfirm =
    !loadingPreview && !!preview && blockers.length === 0 && typedName === companyName && checked;

  const handleConfirm = () => {
    if (!canConfirm || deleting) return;
    setDeleting(true);
    setDeleteError('');
    DeleteAdminCompanyAPI(company.id, typedName, (response) => {
      setDeleting(false);
      if (!response || response.success === false) {
        setDeleteError(response?.error || 'Erro ao excluir a empresa. Tente novamente.');
        return;
      }
      if (onDeleted) onDeleted(response);
    });
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title="Excluir empresa e todos os dados"
      variant="danger"
      confirmLabel="Excluir permanentemente"
      cancelLabel="Cancelar"
      onConfirm={handleConfirm}
      onCancel={onCancel}
      confirmDisabled={!canConfirm}
      loading={deleting}
    >
      {loadingPreview ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : previewError ? (
        <Alert severity="error">{previewError}</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {companyName}
          </Typography>

          <Typography variant="body2" sx={{ color: '#dc2626', fontWeight: 600 }}>
            Esta ação é permanente e não pode ser desfeita.
          </Typography>

          {blockers.map((b) => (
            <Alert key={b.code} severity="error">
              {b.message}
            </Alert>
          ))}

          {warnings.map((w) => (
            <Alert key={w.code} severity="warning">
              {w.message}
            </Alert>
          ))}

          {summary.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Dados que serão apagados
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {summary.map((item) => (
                  <Box
                    key={item.table}
                    sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', color: 'text.secondary' }}
                  >
                    <span>{item.label}</span>
                    <span style={{ fontWeight: 600 }}>{item.count}</span>
                  </Box>
                ))}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.8125rem',
                    mt: 0.5,
                    pt: 0.5,
                    borderTop: '1px solid #eee',
                    color: 'text.primary',
                  }}
                >
                  <span>Total de registros</span>
                  <span style={{ fontWeight: 700 }}>{totalRecords}</span>
                </Box>
              </Box>
            </Box>
          )}

          {files.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Arquivos que serão removidos
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: '0.8125rem', color: 'text.secondary' }}>
                {files.map((f) => (
                  <li key={f.path}>
                    {f.path}
                    {!f.exists ? ' (não encontrado)' : ''}
                  </li>
                ))}
              </Box>
            </Box>
          )}

          <TextField
            size="small"
            label="Digite o nome exato da empresa para confirmar"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            helperText={`Nome esperado: "${companyName}"`}
            fullWidth
            disabled={deleting}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={deleting}
              />
            }
            label="Entendo que todos os dados desta empresa serão apagados permanentemente"
          />

          {deleteError && <Alert severity="error">{deleteError}</Alert>}
        </Box>
      )}
    </ConfirmDialog>
  );
}

export default DeleteCompanyDialog;
